/**
 * Parses a search string into free-text and field-specific filters.
 *
 * Supported prefixes:
 *   cage:      display_cage_number
 *   mouse:     physical_tag (alias: tag:)
 *   tag:       physical_tag (alias for mouse:)
 *   strain:    strain_name
 *   location:  cage location
 *   sex:       sex field
 *   locus:     genotype locus
 *   allele:    genotype allele
 *   notes:     notes field
 *
 * Examples:
 *   "cage: Blue-42 strain: C57"  → { freeText: '', fieldFilters: { cage: 'Blue-42', strain: 'C57' } }
 *   "ERKKTR cage: 101"           → { freeText: 'ERKKTR', fieldFilters: { cage: '101' } }
 *   'cage:"my cage" free'        → { freeText: 'free', fieldFilters: { cage: 'my cage' } }
 */

const KNOWN_PREFIXES = new Set([
  'cage', 'mouse', 'tag', 'strain', 'location', 'sex', 'locus', 'allele', 'notes',
]);

// Normalize prefix aliases to a canonical key
const ALIAS_MAP = {
  tag: 'mouse',
};

/**
 * Tokenizes a search string, respecting quoted values.
 * Returns an array of raw token strings like "cage:Blue-42" or "freeword".
 */
function tokenize(input) {
  const tokens = [];
  const regex = /(\w+):\s*("(?:[^"\\]|\\.)*"|\S+)|(\S+)/g;
  let match;
  while ((match = regex.exec(input)) !== null) {
    if (match[1] !== undefined) {
      // field:value token
      tokens.push({ type: 'field', key: match[1].toLowerCase(), value: match[2].replace(/^"|"$/g, '') });
    } else {
      // bare word
      tokens.push({ type: 'free', value: match[3] });
    }
  }
  return tokens;
}

/**
 * Parses a search string into structured parts.
 * @param {string} input
 * @returns {{ freeText: string, fieldFilters: Object.<string, string> }}
 */
export function parseSearchQuery(input) {
  if (!input || !input.trim()) {
    return { freeText: '', fieldFilters: {} };
  }

  const tokens = tokenize(input.trim());
  const freeWords = [];
  const fieldFilters = {};

  for (const token of tokens) {
    if (token.type === 'field' && KNOWN_PREFIXES.has(token.key)) {
      const canonical = ALIAS_MAP[token.key] || token.key;
      // Last wins if same prefix used multiple times (simple behavior)
      fieldFilters[canonical] = token.value;
    } else {
      freeWords.push(token.value);
    }
  }

  return {
    freeText: freeWords.join(' '),
    fieldFilters,
  };
}

/**
 * Appends field-specific search params to a URLSearchParams object.
 * @param {URLSearchParams} params
 * @param {{ freeText: string, fieldFilters: Object.<string, string> }} parsed
 */
export function applySearchParams(params, parsed) {
  if (parsed.freeText) {
    params.set('search', parsed.freeText);
  }
  const PARAM_MAP = {
    cage: 'search_cage',
    mouse: 'search_tag',
    strain: 'search_strain',
    location: 'search_location',
    sex: 'search_sex',
    locus: 'search_locus',
    allele: 'search_allele',
    notes: 'search_notes',
  };
  for (const [field, value] of Object.entries(parsed.fieldFilters)) {
    const paramName = PARAM_MAP[field];
    if (paramName && value) {
      params.set(paramName, value);
    }
  }
}
